import { StaticPropsComponentExtensions } from '@shared/feature/component-static-extensions';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ListOfficerFacade } from '@shared/feature/list-officer';
import { DateRange, RangePickerPopupComponent, DateRangeTerm } from '@shared/ui/datetime';
import { SelectOption } from '@shared/ui/inputs';
import { SimpleTableComponent, TableData, TableDataColumn, TableDataColumnFilter, TableDataColumnFilterType, TableQueryService } from '@shared/ui/tables';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  ReplaySubject,
  startWith,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { ListEvidenceDateRangeSearch } from '../../models/list-evidence.models';
import { ListEvidenceFacade } from '../../state';
import { AgencyDetailsFacade } from '@shared/feature/agency-details';
import { EvidenceBase, EvidenceType } from '@shared/utils/evidence-management';
import { BaseTableFiltersLocalStorageService } from '@shared/feature/local-storage-list-evidence';
import { BaseAuthService, BaseUser } from '@shared/utils/base-auth';
import { CacheEvidencesInitiator, CacheService } from '@shared/utils/cache';
import { TranslationFromJsonService } from '@shared/feature/translate';

@Component({
  selector: 'feature-list-evidence-list-evidence',
  templateUrl: './list-evidence.component.html',
  styleUrls: ['./list-evidence.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ListEvidenceComponent extends StaticPropsComponentExtensions implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @Input() columns = new Array<TableDataColumn>();
  @Input() defaultSortingColumn?: TableDataColumn | null;
  @Input() onlySharedWithMe = false;
  @Input() title?: string;

  @Input() showOfficerFilter = true;
  @Input() showCategoryFilter = true;
  @Input() showEvidenceFilter = true;
  @Input() showDateFilter = true;

  @Output() enterLockCode = new EventEmitter<string>();
  @Output() navigateToEvidence = new EventEmitter<string>();
  @Output() checkViewChanges = new EventEmitter<void>();

  @ContentChild('templateOuter') templateOuter!: TemplateRef<unknown>;
  @ContentChild('templateHeaderOuter') templateHeaderOuter!: TemplateRef<unknown>;
  @ContentChild('templateHeaderButtons') templateHeaderButtons!: TemplateRef<unknown>;
  @ViewChild(RangePickerPopupComponent) rangePicker?: RangePickerPopupComponent;
  @ViewChild(SimpleTableComponent) tableViewChild?: SimpleTableComponent;

  hideFilter = true;
  setToFirstPageOnChange = false;

  data: TableData<EvidenceBase> = new TableData(0, []);
  officerFilterData = new Array<SelectOption>();
  dateRangeSearchOptions = [
    new SelectOption(this.translationService.instant('datetime-or-field-recorded-on-label'), ListEvidenceDateRangeSearch.RecordedOn),
    new SelectOption(this.translationService.instant('datetime-or-field-uploaded-on-label'), ListEvidenceDateRangeSearch.UploadedOn),
    new SelectOption(this.translationService.instant('datetime-or-field-actual-date-label'), ListEvidenceDateRangeSearch.ActualFileDate),
  ];
  searchOptionsFormGroup = new FormGroup({
    date: new FormControl(this.dateRangeSearchOptions[0].value),
    officer: new FormControl(),
    search: new FormControl<string>('', { nonNullable: true }),
    range: new FormControl(),
    type: new FormControl(),
    categories: new FormControl(),
    selectedSearchRangeTerm: new FormControl(),
  });
  categoriesList = new Array<SelectOption>();
  typeList = [
    new SelectOption(this.translationService.instant('file-type-video'), EvidenceType.Video),
    new SelectOption(this.translationService.instant('file-type-audio'), EvidenceType.Audio),
    new SelectOption(this.translationService.instant('file-type-photo'), EvidenceType.Image),
    new SelectOption(this.translationService.instant('file-type-document'), EvidenceType.Document),
    new SelectOption(this.translationService.instant('file-type-other'), EvidenceType.Other),
  ];

  clearFiltersDisplay$?: Observable<boolean>;
  public pageChanged$ = new ReplaySubject<number>();
  public initialization$ = new BehaviorSubject<boolean>(true);

  private ngUnsubscribe = new Subject<void>();
  private orderChanged$ = new ReplaySubject<TableDataColumn>();
  private searchChanged$ = new Subject<void>();
  private clearFiltersChanged$ = new Subject<void>();
  private pageTypeChanged$ = new BehaviorSubject<boolean>(false);
  private queryBuildingSubscription?: Subscription;

  constructor(
    public listEvidenceFacade: ListEvidenceFacade,
    public tableQuery: TableQueryService,
    public listOfficerFacade: ListOfficerFacade,
    public agencyDetailsFacade: AgencyDetailsFacade,
    public storageService: BaseTableFiltersLocalStorageService,
    public baseAuthService: BaseAuthService,
    private cacheService: CacheService,
    private changeDetectornRef: ChangeDetectorRef,
    private translationService: TranslationFromJsonService
  ) {
    super();
  }

  ngOnInit(): void {
    this.pageTypeChanged$
      .pipe(
        switchMap(() => (this.onlySharedWithMe ? this.listEvidenceFacade.getEvidenceSharedWithMe() : this.listEvidenceFacade.getEvidence())),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((evidence) => {
        this.data = evidence;
        this.changeDetectornRef.detectChanges();
      });

    this.listOfficerFacade
      .getOfficersSelectOption()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((x) => (this.officerFilterData = x));

    this.agencyDetailsFacade
      .getAgencyCategories()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((x) => {
        this.categoriesList = [...x.map((category) => new SelectOption(category.name, category.uid))];
      });

    this.setQueryBuildingSubscription();

    this.clearFiltersDisplay$ = combineLatest<[string[] | null, string | null, DateRange | null, string[] | null, string[] | null]>([
      this.searchOptionsFormGroup.controls.officer.valueChanges.pipe(startWith(null)),
      this.searchOptionsFormGroup.controls.search.valueChanges.pipe(startWith(null)),
      this.searchOptionsFormGroup.controls.range.valueChanges.pipe(startWith(null)),
      this.searchOptionsFormGroup.controls.type.valueChanges.pipe(startWith(null)),
      this.searchOptionsFormGroup.controls.categories.valueChanges.pipe(startWith(null)),
    ]).pipe(map(([a, b, c, d, e]) => !!((a && a?.length > 0) || (b && b !== '') || c || (d && d?.length > 0) || (e && e?.length > 0))));

    this.storageService.storageFiltersSet.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => this.changeDetectornRef.detectChanges());
  }

  ngAfterViewInit(): void {
    this.initialization$.next(this.onlySharedWithMe); // When list evidence is opened, this is set to false
    // and the table loading skeleton is hidden.

    combineLatest<[BaseUser | undefined, boolean]>([this.baseAuthService.getCurrentBaseUser(), this.pageTypeChanged$])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(([user]) => {
        this.storageService.setUpServiceForComponent(
          this.onlySharedWithMe ? `SharedWithMe-${user?.uid}` : `ListEvidence-${user?.uid}`,
          this.searchOptionsFormGroup,
          this.orderChanged$,
          this.tableViewChild,
          this.clearFiltersChanged$,
          this.pageChanged$
        );

        this.hideFilter = !this.storageService.checkIfAdvancedFiltersAreSet();
        this.checkViewChanges.emit();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const onlySharedWithMeChanges = changes['onlySharedWithMe'];
    if (!onlySharedWithMeChanges.isFirstChange() && onlySharedWithMeChanges.previousValue !== onlySharedWithMeChanges.currentValue) {
      if (onlySharedWithMeChanges.previousValue === false && onlySharedWithMeChanges.currentValue === true) {
        this.initialization$.next(true); // Changing from list evidence to shared with me - the data needs to be fetched from the API
      } else {
        this.initialization$.next(false); // To ensure the subject does not hold the skeleton if list evidence is opened
      }
      this.pageTypeChanged();
    }
  }

  clearFilters(): void {
    this.tableViewChild?.setArrowDirectionOnDefaultSortingColumn();
    if (this.defaultSortingColumn) {
      this.orderChanged$.next(this.defaultSortingColumn);
    }
    this.resetForm();
    this.rangePicker?.reset();
    this.clearFiltersChanged$.next();
    this.hideFilter = true;
    this.checkViewChanges.emit();
  }

  order(data: TableDataColumn): void {
    this.orderChanged$.next(data);
  }

  pageChanged(page: number): void {
    this.pageChanged$.next(page);
  }

  dateFilterChanged(dateRangeTerm: DateRangeTerm): void {
    this.searchOptionsFormGroup.controls.range.setValue(dateRangeTerm.dateRange);
    this.searchOptionsFormGroup.controls.selectedSearchRangeTerm.setValue(dateRangeTerm.searchRangeTerm);
  }

  search(): void {
    this.searchChanged$.next();
  }

  updateEvidenceList(): void {
    this.clearFiltersChanged$.next();
  }

  onDoubleClick(index: number): void {
    if (this.data.data[index].isLocked) {
      this.enterLockCode.emit(this.data.data[index].uid);
    } else {
      this.navigateToEvidence.emit(this.data.data[index].uid);
    }
  }

  ngOnDestroy(): void {
    this.cacheService.evidencesDataModified(CacheEvidencesInitiator.PageLeave);
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private pageTypeChanged(): void {
    this.setToFirstPageOnChange = false;
    this.storageService.resetService();
    this.resetForm();

    this.queryBuildingSubscription?.unsubscribe();
    this.pageChanged$ = new ReplaySubject<number>(1);
    this.orderChanged$ = new ReplaySubject<TableDataColumn>(1);
    this.setQueryBuildingSubscription();

    if (this.storageService.autoClearFiltersOn()) {
      this.tableViewChild?.setArrowDirectionOnDefaultSortingColumn();
    }
    this.checkViewChanges.emit();
    this.pageTypeChanged$.next(true);
  }

  private setQueryBuildingSubscription(): void {
    this.queryBuildingSubscription = combineLatest<
      [DateRange | null, string | null, string[], string[], string[], TableDataColumn | null, number, string | null, void | null, void | null, void]
    >([
      this.searchOptionsFormGroup.controls.range.valueChanges.pipe(
        startWith(null),
        tap(() => this.checkToSetToInitialPage())
      ),
      this.searchOptionsFormGroup.controls.date.valueChanges.pipe(startWith(this.dateRangeSearchOptions[0].value)),
      this.searchOptionsFormGroup.controls.officer.valueChanges.pipe(
        startWith(null),
        distinctUntilChanged(),
        tap(() => this.checkToSetToInitialPage())
      ),
      this.searchOptionsFormGroup.controls.type.valueChanges.pipe(
        startWith(null),
        distinctUntilChanged(),
        tap(() => this.checkToSetToInitialPage())
      ),
      this.searchOptionsFormGroup.controls.categories.valueChanges.pipe(
        startWith(null),
        distinctUntilChanged(),
        tap(() => this.checkToSetToInitialPage())
      ),
      this.orderChanged$.pipe(
        startWith(this.defaultSortingColumn ?? null),
        tap(() => this.checkToSetToInitialPage())
      ),
      this.pageChanged$.pipe(startWith(1)),
      this.listEvidenceFacade.getEvidenceQuery(),
      this.searchChanged$.pipe(
        startWith(null),
        tap(() => this.checkToSetToInitialPage())
      ),
      this.clearFiltersChanged$.pipe(
        startWith(null),
        tap(() => this.checkToSetToInitialPage())
      ),
      this.storageService.storageFiltersSet,
    ])
      .pipe(debounceTime(300), takeUntil(this.ngUnsubscribe))
      .subscribe(([dateRange, dateType, officerList, type, category, ordering, page, evidenceNgrxStoreQuery]) => {
        const dateStartRangeFilterColumn = new TableDataColumnFilter(dateType ?? '', dateRange?.utcStartDate ?? '', TableDataColumnFilterType.GreaterThan);
        const dateEndRangeFilterColumn = new TableDataColumnFilter(dateType ?? '', dateRange?.utcEndDate ?? '', TableDataColumnFilterType.LessThan);
        const officerFilterColumn = new TableDataColumnFilter('Officer.Uid', officerList?.join('|') ?? '', TableDataColumnFilterType.Equals);
        const categoryFilter = new TableDataColumnFilter('categories', category?.join('|') ?? '', TableDataColumnFilterType.Equals);
        const typeFilter = new TableDataColumnFilter('FileType', type?.join('|') ?? '', TableDataColumnFilterType.Equals);
        this.setToFirstPageOnChange = true;

        const newQuery = this.tableQuery.getQuery(
          page,
          10,
          ordering ?? undefined,
          [dateStartRangeFilterColumn, dateEndRangeFilterColumn, officerFilterColumn, categoryFilter, typeFilter],
          this.searchOptionsFormGroup.controls.search.value
        );

        if (this.onlySharedWithMe) {
          this.listEvidenceFacade.sharedWithMeEvidence(newQuery);
        } else if (newQuery !== evidenceNgrxStoreQuery && evidenceNgrxStoreQuery !== '') {
          this.listEvidenceFacade.listEvidence(newQuery);
        }
        this.initialization$.next(false); // This line ensures that the skeleton is not hidden early. In case we are dealing with list evidence,
        // the skeleton is already hidden either in the ngAfterViewInit, or in the ngOnChanges. But when only shared with me is opened,
        // the data may not be up to date and the table may change a few times. Changing the value held in initialization$ subject
        // ensures the skeleton is kept until the isSharedWithMeLoading is set to true, which thereafter will keep the skeleton
        // as long as it is needed.
      });
  }

  private resetForm(): void {
    this.searchOptionsFormGroup.controls.officer.reset();
    this.searchOptionsFormGroup.controls.search.reset();
    this.searchOptionsFormGroup.controls.range.reset();
    this.searchOptionsFormGroup.controls.categories.reset();
    this.searchOptionsFormGroup.controls.type.reset();
    this.searchOptionsFormGroup.controls.date.setValue(this.dateRangeSearchOptions[0].value);
    this.searchOptionsFormGroup.controls.selectedSearchRangeTerm.setValue(undefined);
  }

  private checkToSetToInitialPage(): void {
    if (this.setToFirstPageOnChange) {
      this.pageChanged$.next(1);
    }
  }
}
